import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import Link from "next/link"
import FormField from "./FormField"
import Sidebar from "./Sidebar"
import { useTranslations } from "lib/useTranslations.client"
import { recordRepContactClick } from "lib/reps/clicks"
import { preselectCountryCode } from "lib/reps/visitorCountry"
import usePageData from "lib/usePageData"
import {
  FILTER_BY_REGION,
  findRepresentatives,
  formatLanguages,
  repPlace,
  headquartersOnlyDirectory,
  initials,
  mailtoHref,
  placeCode,
  telHref,
} from "lib/reps/directory"
import { countries, needsSubdivision, subdivisionsFor } from "lib/reps/territories"
import { postLead } from "lib/leadFormClient"
import LeadFormError from "./LeadFormError"

export default function Contact({ repDirectory, globalSettings = {} }) {
  const { t } = useTranslations()
  const pageData = usePageData()
  const directory = repDirectory || headquartersOnlyDirectory()
  const selectionTouched = useRef(false)
  const initialCountry = preselectCountryCode(pageData?.visitorCountryCode)

  const [isOpen, setIsOpen] = useState(false)
  const [countryCode, setCountryCode] = useState(initialCountry)
  const [subdivisionCode, setSubdivisionCode] = useState("")

  useEffect(() => {
    if (selectionTouched.current || countryCode) return undefined

    let cancelled = false
    fetch("/api/visitor-country", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled || selectionTouched.current) return
        const next = preselectCountryCode(body?.country)
        if (next) setCountryCode((current) => current || next)
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [countryCode])

  const closeModal = () => {
    setIsOpen(false)
  }

  const onCountryChange = (event) => {
    selectionTouched.current = true
    setCountryCode(event.target.value)
    setSubdivisionCode("")
  }

  const showSubdivision = FILTER_BY_REGION && needsSubdivision(countryCode)
  const selectionComplete = Boolean(countryCode) && (!showSubdivision || subdivisionCode)
  const code = placeCode({ countryCode, subdivisionCode })
  const match = selectionComplete
    ? findRepresentatives(directory, { countryCode, subdivisionCode })
    : null

  const onContactAction = (action, rep) => {
    recordRepContactClick({
      action,
      rep,
      code,
    })
  }

  return (
    <div>
      <button
        className="btn btn-primary fixed bottom-4 lg:bottom-8 right-4 lg:right-8 z-contact-button"
        aria-expanded={isOpen}
        aria-controls="contact-panel"
        aria-haspopup="dialog"
        onClick={() => setIsOpen((open) => !open)}
        data-testid="contact-open"
      >
        {t("general.contact-us")}
      </button>
      <Sidebar isOpen={isOpen} onClose={closeModal}>
        <div id="contact-panel" className="p-4 lg:p-8">
          <h2 id="contact-heading" className="text-2xl mb-10">
            Contact us to learn more
          </h2>
          <form aria-labelledby="contact-heading" onSubmit={(event) => event.preventDefault()}>
            <TerritorySelect
              id="rep-country"
              label="Country"
              placeholder="Country *"
              value={countryCode}
              onChange={onCountryChange}
              options={countries()}
              autoComplete="country"
              required
            />
            {showSubdivision && (
              <TerritorySelect
                id="rep-subdivision"
                label="State/Province"
                placeholder="State/Province"
                value={subdivisionCode}
                onChange={(event) => {
                  selectionTouched.current = true
                  setSubdivisionCode(event.target.value)
                }}
                options={subdivisionsFor(countryCode)}
                autoComplete="address-level1"
                required
              />
            )}
            <div aria-live="polite" aria-atomic="true">
              {match?.reps.map((rep, index) => (
                <RepCard
                  key={rep.id || rep.email || index}
                  rep={rep}
                  placeCode={code}
                  onContactAction={onContactAction}
                />
              ))}
            </div>
            <noscript>
              <p className="mt-6 text-base">
                <a href={mailtoHref(directory.headquarters)}>
                  {directory.headquarters.email}
                </a>
                <br />
                <a href={telHref(directory.headquarters.phone)}>
                  {directory.headquarters.phone}
                </a>
              </p>
            </noscript>
          </form>
          <GeneralInquiryForm t={t} globalSettings={globalSettings} />
        </div>
      </Sidebar>
    </div>
  )
}

function TerritorySelect({
  id,
  label,
  placeholder,
  value,
  onChange,
  options,
  autoComplete,
  required,
  disabled,
}) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          aria-required={required}
          className={classNames(
            "w-full appearance-none rounded-full border border-grayed-muted bg-white px-4 py-2 pr-10 text-lg",
            "focus:border-primary focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
            value ? "text-primary" : "text-grayed",
            disabled && "cursor-not-allowed opacity-60"
          )}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        <Chevron />
      </div>
    </div>
  )
}

function RepCard({ rep, placeCode: code, onContactAction }) {
  const callHref = telHref(rep.phone)
  const emailHref = mailtoHref(rep, code)
  const monogram = initials(rep.name)
  const languages = formatLanguages(rep.languages).split(", ").filter(Boolean)
  const place = rep.isHeadquarters ? "" : repPlace(rep)
  const actions = [
    emailHref ? { action: "email", href: emailHref, label: "Send an email", primary: false } : null,
    callHref ? { action: "call", href: callHref, label: "Call", primary: true } : null,
  ].filter(Boolean)

  return (
    <section className="mt-8 rounded-2xl border border-grayed-muted p-4" aria-label={rep.name}>
      <div className="flex items-center gap-3">
        {monogram && (
          <span
            aria-hidden="true"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-base tracking-wide text-primary"
          >
            {monogram}
          </span>
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="min-w-0 break-words text-lg text-primary">{rep.name}</h3>
            {languages.map((language) => (
              <span key={language} className="rounded-full border border-grayed-muted px-2 py-0.5 text-xs text-primary/70">
                {language}
              </span>
            ))}
          </div>
          {place && <p className="text-base text-primary/70">{place}</p>}
          {rep.title && <p className="text-base text-primary/70">{rep.title}</p>}
          {callHref && (
            <p className="text-xs text-grayed leading-[140%]">
              <a
                href={callHref}
                onClick={() => onContactAction("call", rep)}
                className="break-words underline underline-offset-2"
              >
                {rep.phone}
              </a>
            </p>
          )}
          {emailHref && (
            <p className="text-xs text-grayed leading-[140%]">
              <a
                href={emailHref}
                onClick={() => onContactAction("email", rep)}
                className="break-all underline underline-offset-2"
              >
                {rep.email}
              </a>
            </p>
          )}
        </div>
      </div>
      {actions.length > 0 && (
        <div className={classNames("mt-4 grid gap-2", actions.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
          {actions.map((item) => (
            <a
              key={item.action}
              href={item.href}
              onClick={() => onContactAction(item.action, rep)}
              className={classNames(
                "flex min-w-0 items-center justify-center rounded-2xl border border-primary px-2 py-3 text-center text-base transition-colors duration-75",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                item.primary
                  ? "bg-black text-white hover:bg-white hover:text-black"
                  : "bg-white text-black hover:bg-black hover:text-white"
              )}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </section>
  )
}

function GeneralInquiryForm({ t, globalSettings }) {
  const initialInputs = { name: "", email: "", message: "" }
  const [inputs, setInputs] = useState(initialInputs)
  const [status, setStatus] = useState(false)

  const onInputChange = (event) => {
    setInputs((input) => ({
      ...input,
      [event.target.name]: event.target.value,
    }))
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    if (status === "LOADING") return

    setStatus("LOADING")
    const result = await postLead("/api/contact-form", inputs)
    if (!result.ok) {
      setStatus("ERROR")
      return
    }

    event.target.reset()
    setInputs(initialInputs)
    setStatus("SUCCESS")
  }

  return (
    <form className="mt-12" aria-labelledby="general-inquiry-heading" onSubmit={onSubmit} data-testid="contact-form">
      <h3 id="general-inquiry-heading" className="text-xl mb-6">
        General inquiry
      </h3>
      <FormField
        name="name"
        placeholder={t("forms.name")}
        onChange={onInputChange}
        value={inputs.name}
        required
      />
      <FormField
        name="email"
        placeholder={t("forms.email")}
        type="email"
        onChange={onInputChange}
        value={inputs.email}
        required
      />
      <FormField
        name="message"
        type="textarea"
        placeholder={t("forms.contact-message")}
        onChange={onInputChange}
        value={inputs.message}
        required
      />
      {status === "SUCCESS" && (
        <p data-testid="contact-success">{t("forms.contact-success")}</p>
      )}
      {status === "ERROR" && (
        <LeadFormError
          testId="contact-error"
          settings={globalSettings}
          message={t("forms.error", "Something went wrong and your message was not sent. Please try again.")}
        />
      )}
      <button
        type="submit"
        className="btn btn-primary mt-10"
        disabled={status === "LOADING"}
        data-testid="contact-submit"
      >
        {status === "LOADING" ? t("forms.sending", "Sending…") : t("forms.submit")}
      </button>
      <span className="mx-4">{t("general.or")}</span>
      <Link href="/calculator">
        <a className="btn btn-secondary">{t("general.calculate-estimate")}</a>
      </Link>
    </form>
  )
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
